import { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';
import React from 'react';

interface Props {
  children: React.ReactNode;
  renderError?: (error: unknown) => React.ReactNode;
  extraErrorData?: () => Record<string, unknown>;
  sentryErrorLevel?: Sentry.SeverityLevel;
  fallback?: React.ReactElement | Sentry.FallbackRender | undefined;
}

const getCaptureOptions = (extraErrorData: object) => {
  const stringifiedExtraErrorData = JSON.stringify(extraErrorData);

  // ensure that extra error data wouldn't cause payload to exceed size limit.
  // 50000 was arrived at via trial and error, by running the following snippet
  // and observing the network tab to see when requests start to fail.
  // the largest sent payload was found to have a length of 60950, so 50k
  // should give us about a 10k buffer
  // Sentry.captureException(new Error('test payload size'), {extra: {document: new Array(60000).fill('a').join('')}})
  // more info about payload size limits can be found:
  // https://docs.sentry.io/platforms/javascript/enriching-events/context/#size-limitations
  const limit = 50000;
  if (stringifiedExtraErrorData.length < limit) {
    return {
      extra: extraErrorData,
    };
  }

  return {
    extra: {
      truncated: true,
      truncatedExtraErrorData: stringifiedExtraErrorData.slice(0, limit),
    },
  };
};

export const SharedErrorBoundary = ({
  children,
  extraErrorData,
  fallback,
  sentryErrorLevel = 'fatal',
}: Props) => (
  <Sentry.ErrorBoundary
    beforeCapture={(scope, error) => {
      scope.setLevel(sentryErrorLevel);

      let extraData = extraErrorData?.();
      if (error instanceof ApolloError) {
        extraData = extraData ?? {};
        extraData.graphQLErrors = JSON.stringify(error.graphQLErrors);
        extraData.clientErrors = error.clientErrors;
        extraData.networkError = error.networkError;
        extraData.extraInfo = error.extraInfo;
      }
      if (extraData) scope.setExtras(getCaptureOptions(extraData));
    }}
    fallback={fallback}
  >
    {children}
  </Sentry.ErrorBoundary>
);
